import { useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  ArrowLeftOnRectangleIcon,
  CheckIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Action, Button, Label } from "@sablier/v2-components/molecules";
import { links } from "@sablier/v2-constants";
import { useCopy, useShortAddress } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { vendors } from "@sablier/v2-utils";
import {
  useAccount,
  useModalAccount, //  useModalApproval,
  useModalPermissions,
  useModalProxy, //  useSessionApproval,
} from "~/client/hooks";

const WrapperPartial = styled.div`
  width: 100%;
  padding: ${(props) => props.theme.sizes.edge} 0;
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1 / 2)
      ${(props) => props.theme.sizes.modalEdge};
  }
`;

const Group = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;

    &:not(:last-child) {
      padding-bottom: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      border-bottom: 2px solid ${(props) => props.theme.colors.border};
    }
  }
`;

const ActionWarning = styled(Action)`
  p {
    color: ${(props) => props.theme.colors.yellow};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding: calc(${(props) => props.theme.sizes.edge} * 1 / 2) 0;
    ${List} {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 2);
    }
    ${Group} {
      padding-inline: 0;
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Group} {
      padding-inline: 0;
    }
    ${List} {
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
  }
`;

function Body() {
  const { doClose } = useModalAccount();
  // const { doOpen: doOpenApproval } = useModalApproval();
  const { doOpen: doOpenPermissions } = useModalPermissions();
  const { doOpen: doOpenProxy } = useModalProxy();
  const { address, disconnect, isHostSafe, proxy } = useAccount();
  // const { approval } = useSessionApproval();
  const shortAddress = useShortAddress(address, 10, -6);
  const shortProxy = useShortAddress(proxy, 10, -6);

  const shorterAddress = useShortAddress(address, 5, -4);
  const shorterProxy = useShortAddress(proxy, 5, -4);

  const { t } = useT();

  const [isCopied, doCopy] = useCopy(address || "");
  const isProxyAvailable = useMemo(() => _.isEthereumAddress(proxy), [proxy]);

  const doDisconnect = useCallback(() => {
    vendors.track.log("clickOnDisconnect");
    if (disconnect) {
      disconnect();
    }
    doClose();
  }, [disconnect, doClose]);

  // const openApproval = useCallback(() => {
  //   vendors.track.log((events) => {
  //     return events.openModalFrom({
  //       nameKey: "approval",
  //       placeKey: "accountModal",
  //     });
  //   });
  //   doOpenApproval();
  //   doClose();
  // }, [doClose, doOpenApproval]);

  const openProxy = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "proxy",
        placeKey: "accountModal",
      });
    });
    doOpenProxy();
    doClose();
  }, [doClose, doOpenProxy]);

  const openPermissions = useCallback(() => {
    vendors.track.log((events) => {
      return events.openModalFrom({
        nameKey: "permissions",
        placeKey: "accountModal",
      });
    });
    doOpenPermissions();
    doClose();
  }, [doClose, doOpenPermissions]);

  return (
    <Wrapper>
      <List>
        <Group>
          <Label value={t("words.account")} icon={UserIcon} />
          <Action
            title={t("modalCards.account.current", { address: shortAddress })}
            titleSmall={t("modalCards.account.currentSmall", {
              address: shorterAddress,
            })}
          >
            {isCopied ? (
              <Button
                accent={"iconic400"}
                appearance={"outline"}
                isMini
                right={CheckIcon}
                title={t("words.copied")}
              />
            ) : (
              <Button
                accent={"dark200"}
                appearance={"solid"}
                isMini
                right={address}
                rightPurpose={"identicon"}
                onClick={doCopy}
                title={t("words.copy")}
              />
            )}
          </Action>

          {isProxyAvailable ? (
            <Action
              title={t("modalCards.account.proxy", { address: shortProxy })}
              titleSmall={t("modalCards.account.proxySmall", {
                address: shorterProxy,
              })}
            >
              <Button
                accent={"dark200"}
                appearance={"solid"}
                isMini
                right={proxy}
                rightPurpose={"identicon"}
                onClick={openProxy}
                title={t("words.configure")}
              />
            </Action>
          ) : (
            <ActionWarning title={t("modalCards.account.configure")}>
              <Button
                accent={"warning200"}
                appearance={"solid"}
                isMini
                right={ShieldCheckIcon}
                onClick={openProxy}
                title={t("words.configure")}
              />
            </ActionWarning>
          )}
        </Group>

        <Group>
          <Label value={t("words.settings")} icon={Cog6ToothIcon} />
          {/* <Action
            title={t("modalCards.account.approval", {
              approval:
                approval == "permit2"
                  ? t("words.permit2")
                  : t("structs.erc20Approval"),
            })}
            titleSmall={t("modalCards.account.approvalSmall")}
          >
            <Button
              accent={"dark200"}
              appearance={"solid"}
              isMini
              onClick={openApproval}
              right={ShieldCheckIcon}
              title={_.capitalize(t("words.change"))}
            />
          </Action> */}
          <Action title={t("modalCards.account.cookies")}>
            <Button
              accent={"dark200"}
              appearance={"solid"}
              isMini
              onClick={openPermissions}
              right={Cog6ToothIcon}
              title={_.capitalize(t("words.manage"))}
            />
          </Action>
        </Group>
        <Group>
          <Action title={t("modalCards.account.light")}>
            <Button
              accent={"dark200"}
              appearance={"solid"}
              isMini
              isDisabled
              title={t("structs.comingSoon")}
            />
          </Action>
          <Action title={t("modalCards.account.feature")}>
            <Button
              accent={"dark200"}
              appearance={"solid"}
              isMini
              purpose={"external"}
              to={links.canny}
              title={_.capitalize(t("words.request"))}
            />
          </Action>
          <Action title={t("modalCards.account.v1")}>
            <Button
              accent={"dark200"}
              appearance={"solid"}
              isMini
              purpose={"external"}
              to={links.v1.app}
              title={_.capitalize(t("structs.goToV1"))}
            />
          </Action>
          {!isHostSafe && (
            <Action
              title={t("modalCards.account.disconnect")}
              titleSmall={t("modalCards.account.disconnectSmall")}
            >
              <Button
                accent={"dark200"}
                appearance={"solid"}
                isMini
                onClick={doDisconnect}
                right={ArrowLeftOnRectangleIcon}
                title={t("words.disconnect")}
              />
            </Action>
          )}
        </Group>
      </List>
    </Wrapper>
  );
}

export default Body;
